import { useState } from "react";
import { Download, Eye, EyeOff, Paperclip, Upload } from "lucide-react";
import { Bouton } from "@/composants/ui/bouton";
import { Carte, CorpsCarte, EnTeteCarte } from "@/composants/ui/carte";
import { ChampSelection } from "@/composants/ui/champ";
import { Chargement, Erreur, Vide } from "@/composants/ui/etats";
import { Etiquette } from "@/composants/ui/etiquette";
import { messageDe, useChargement } from "@/lib/chargement";
import { categorieDocument, horodatage, octets } from "@/lib/libelles";
import { useReferentiel } from "@/lib/referentiels";
import { api, urlContenuDocument, type CibleDocument, type Document } from "@/lib/api";
import { useSession } from "@/session";

/**
 * Les pièces d'un dossier ou d'un lot — déposer, télécharger, publier.
 *
 * ADR-0011 : aucun fichier n'est servi en statique. Le téléchargement passe par
 * un lien vers `/api/documents/{id}/contenu`, qui vérifie profil et affectation
 * avant de rendre le moindre octet. Les photos de réception montrent
 * l'intérieur du domicile de particuliers.
 *
 * **Publier est un acte explicite et daté**, jamais un défaut de configuration :
 * le bouton dit ce qu'il fait, et l'état se lit en toutes lettres (ADR-0024).
 */

export type DestinationPossible = { valeur: string; libelle: string; cible: CibleDocument };

export function Documents({
  cible,
  destinations,
  categorieProposee = "PLAN",
  ecriture = true,
}: {
  /** Ce qu'on énumère : un dossier (avec ses lots) ou un seul lot. */
  cible: CibleDocument;
  /** Où l'on peut déposer. Une seule entrée sur une fiche lot. */
  destinations: DestinationPossible[];
  categorieProposee?: string;
  /** Faux quand on consulte la commande sans pouvoir y agir (ADR-0032). */
  ecriture?: boolean;
}) {
  const session = useSession();
  const identite = session.statut === "connecte" ? session.identite : null;
  const peutDeposer = ecriture && (identite?.permissions.includes("DEPOSER_DOCUMENT") ?? false);
  const peutPublier =
    ecriture && (identite?.permissions.includes("PUBLIER_DOCUMENT_CLIENT") ?? false);

  const cle = JSON.stringify(cible);
  const { donnees, erreur, enCours, recharger } = useChargement<Document[]>(
    (signal) => api.documents(cible, signal),
    [cle],
  );
  const [depot, setDepot] = useState(false);
  const [action, setAction] = useState<string | null>(null);
  // ADR-0062 : les types vivent en base. Leur libellé s'y lit, désactivés compris :
  // un document ancien garde le sien.
  const types = useReferentiel("types-document");
  const libelleType = (code: string) =>
    types.donnees?.valeurs.find((v) => v.code === code)?.libelle ?? categorieDocument(code);

  async function basculerPublication(document: Document) {
    setAction(null);
    try {
      await api.publierDocument(document.id, !document.visible_client);
      recharger();
    } catch (cause) {
      setAction(messageDe(cause));
    }
  }

  return (
    <Carte>
      <EnTeteCarte className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-encre">
          <Paperclip className="h-4 w-4 text-encre-douce" aria-hidden />
          Documents{donnees ? ` (${donnees.length})` : ""}
        </h2>
        {peutDeposer ? (
          <Bouton taille="compact" onClick={() => setDepot((ouvert) => !ouvert)}>
            <Upload className="h-4 w-4" aria-hidden />
            {depot ? "Fermer" : "Déposer"}
          </Bouton>
        ) : null}
      </EnTeteCarte>

      <CorpsCarte className="flex flex-col gap-4">
        {action ? <Erreur message={action} /> : null}
        {erreur ? <Erreur message={erreur} onReessayer={recharger} /> : null}

        {depot ? (
          <FormulaireDepot
            destinations={destinations}
            categorieProposee={categorieProposee}
            onDepose={() => {
              setDepot(false);
              recharger();
            }}
            onAnnuler={() => setDepot(false)}
          />
        ) : null}

        {!erreur && enCours ? <Chargement message="Chargement des documents…" /> : null}

        {donnees && donnees.length === 0 && !depot ? (
          <Vide
            titre="Aucun document"
            detail="Le bon de commande signé, les plans et les photos se déposent ici."
          />
        ) : null}

        {donnees && donnees.length > 0 ? (
          <ul className="divide-y divide-filet">
            {donnees.map((document) => (
              <li
                key={document.id}
                className="flex flex-col gap-2 py-3 first:pt-0 sm:flex-row sm:items-center sm:gap-4"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-encre">
                    {document.nom_fichier_origine}
                  </p>
                  <p className="text-sm text-encre-douce">
                    {libelleType(document.categorie)}
                    {" — "}
                    {document.code_lot ?? "sur la Commande / Projet"}
                  </p>
                  <p className="text-xs text-encre-douce">
                    {octets(document.taille_octets)} · {horodatage(document.cree_le)}
                    {document.depose_par ? ` · déposé par ${document.depose_par}` : ""}
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {/* L'état se lit en toutes lettres, jamais par la couleur seule. */}
                  <Etiquette ton={document.visible_client ? "actif" : "neutre"}>
                    {document.visible_client ? "Visible du client" : "Interne"}
                  </Etiquette>

                  <a
                    href={urlContenuDocument(document.id)}
                    className="inline-flex h-9 items-center gap-1.5 rounded-[6px] border border-filet bg-white px-3 text-sm font-medium text-encre hover:bg-chene-clair/40"
                  >
                    <Download className="h-4 w-4" aria-hidden />
                    <span className="sr-only sm:not-sr-only">Télécharger</span>
                  </a>

                  {peutPublier ? (
                    <Bouton
                      taille="compact"
                      variante="discret"
                      onClick={() => void basculerPublication(document)}
                    >
                      {document.visible_client ? (
                        <>
                          <EyeOff className="h-4 w-4" aria-hidden />
                          Retirer au client
                        </>
                      ) : (
                        <>
                          <Eye className="h-4 w-4" aria-hidden />
                          Publier au client
                        </>
                      )}
                    </Bouton>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        ) : null}
      </CorpsCarte>
    </Carte>
  );
}

const FORMATS_DOCUMENT = ".pdf,.jpg,.jpeg,.png,.heic,.webp,.txt,.csv";
const FORMATS_VIDEO = ".mp4,.mov,video/mp4,video/quicktime";

function FormulaireDepot({
  destinations,
  categorieProposee,
  onDepose,
  onAnnuler,
}: {
  destinations: DestinationPossible[];
  categorieProposee: string;
  onDepose: () => void;
  onAnnuler: () => void;
}) {
  const [destination, setDestination] = useState(destinations[0]?.valeur ?? "");
  const [categorie, setCategorie] = useState(categorieProposee);
  const [fichier, setFichier] = useState<File | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  // Seuls les types actifs se proposent : un type désactivé ne s'attribue plus.
  const types = useReferentiel("types-document", { actives: true });
  const options = (types.donnees?.valeurs ?? []).map((v) => ({ valeur: v.code, libelle: v.libelle }));
  // ADR-0061 : une vidéo ne se dépose qu'à ce type, et ce type ne porte qu'une vidéo.
  const video = categorie === "VIDEO_CHANTIER";

  return (
    <form
      className="flex flex-col gap-4 rounded-[6px] border border-filet bg-chene-clair/20 p-4"
      onSubmit={async (evenement) => {
        evenement.preventDefault();
        const choisie = destinations.find((d) => d.valeur === destination);
        if (!fichier || !choisie) return;
        setErreur(null);
        setEnvoi(true);
        try {
          await api.deposerDocument(choisie.cible, categorie, fichier);
          onDepose();
        } catch (cause) {
          setErreur(messageDe(cause));
        } finally {
          setEnvoi(false);
        }
      }}
    >
      {erreur ? <Erreur message={erreur} /> : null}

      <div className="grid gap-4 sm:grid-cols-2">
        <ChampSelection
          libelle="Type de document"
          options={options}
          value={categorie}
          onChange={(evenement) => setCategorie(evenement.target.value)}
        />
        {destinations.length > 1 ? (
          <ChampSelection
            libelle="Rattaché à"
            options={destinations.map(({ valeur, libelle }) => ({ valeur, libelle }))}
            value={destination}
            onChange={(evenement) => setDestination(evenement.target.value)}
          />
        ) : null}
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="depot-fichier" className="text-sm font-medium text-encre">
          Fichier
          <span aria-hidden className="ml-0.5 text-alerte">
            *
          </span>
        </label>
        <input
          id="depot-fichier"
          type="file"
          required
          accept={video ? FORMATS_VIDEO : FORMATS_DOCUMENT}
          onChange={(evenement) => setFichier(evenement.target.files?.[0] ?? null)}
          className="text-sm text-encre file:mr-3 file:rounded-[6px] file:border file:border-filet file:bg-white file:px-3 file:py-2 file:text-sm file:text-encre"
        />
        <p className="text-xs text-encre-douce">
          {video
            ? "Vidéo mp4 ou mov, jusqu'à la taille réglée par l'Administrateur ; un refus dit cette limite."
            : "PDF, image ou texte, 25 Mo au maximum."}{" "}
          Le fichier n'est jamais servi en statique : chaque lecture repasse par un contrôle de
          profil et d'affectation.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <Bouton type="submit" variante="principal" disabled={envoi || !fichier}>
          {envoi ? "Dépôt en cours…" : "Déposer"}
        </Bouton>
        <Bouton type="button" variante="discret" onClick={onAnnuler} disabled={envoi}>
          Annuler
        </Bouton>
      </div>
    </form>
  );
}
